import { FileViewerModal } from "@/components/src/file-viewer";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/dentlab/src/lib/util";
import { useFileUpload } from "@/dentlab/src/hooks/useFileUpload";
import { supabase } from "@/lib/supabase";
import {
	FileInGallery,
	FileGallerySize,
	FileGalleryItem,
} from "@/dentlab/src/components/chat/messages/job-request-custom/components/file-in-message/file-in-gallery";
import { useFileUrlCacheStore } from "@/lib/store/file-url-cache.store";
import { Skeleton } from "@/dentlab/src/components/ui/skeleton";
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogCancel,
	AlertDialogAction,
} from "@/dentlab/src/components/ui/alert-dialog";

const SKELETON_DIMENSION: Record<FileGallerySize, string> = {
	small: "h-24 w-24",
	medium: "h-36 w-36",
	large: "h-48 w-48",
};

export type FilePreviewSize = FileGallerySize;

export const JobFilesTab: React.FC<{
	isExpanded?: boolean;
	previewSize?: FilePreviewSize;
}> = ({ isExpanded = false, previewSize }) => {
	const {
		job,
		uploadFile,
		labJobFilesLookup,
		medJobFilesLookup,
		jobFilesLoadedForJobId,
		shareFile,
		unshareFile,
		hardDeleteFile,
	} = useJobStore((state) => ({
		job: state.job,
		uploadFile: state.uploadFile,
		labJobFilesLookup: state.labJobFilesLookup,
		medJobFilesLookup: state.medJobFilesLookup,
		jobFilesLoadedForJobId: state.jobFilesLoadedForJobId,
		shareFile: state.shareFile,
		unshareFile: state.unshareFile,
		hardDeleteFile: state.hardDeleteFile,
	}));

	const isLoadingFiles = !!job?.id && jobFilesLoadedForJobId !== job.id;

	const { preloadFiles, fileCache, evictFiles } = useFileUrlCacheStore(
		(state) => ({
			preloadFiles: state.preloadFiles,
			fileCache: state.fileCache,
			evictFiles: state.evictFiles,
		})
	);

	const [fileViewerOpen, setFileViewerOpen] = useState(false);
	const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
	const [fileToDeleteId, setFileToDeleteId] = useState<string | null>(null);
	const [deleting, setDeleting] = useState(false);

	const labFiles = Object.values(labJobFilesLookup)
		.filter((file) => !file.job_document_id)
		.sort(
			(a, b) =>
				new Date(a.created_at ?? "").getTime() -
				new Date(b.created_at ?? "").getTime()
		);
	const medFiles = Object.values(medJobFilesLookup).sort(
		(a, b) =>
			new Date(a.created_at ?? "").getTime() -
			new Date(b.created_at ?? "").getTime()
	);
	const allFiles = [...labFiles, ...medFiles];
	const allFileIds = allFiles.map((f) => f.id).filter(Boolean) as string[];
	const hasBothTypes = labFiles.length > 0 && medFiles.length > 0;

	useEffect(() => {
		if (allFileIds.length === 0) return;
		preloadFiles(allFileIds);
	}, [allFileIds.join(",")]);

	const fetchJobFiles = useJobStore((state) => state.fetchJobFiles);
	const jobIdRef = useRef(job?.id);
	jobIdRef.current = job?.id;

	useEffect(() => {
		if (allFileIds.length === 0) return;

		const channels = allFileIds.map((fileId) =>
			supabase
				.channel(`job-files-shared:${fileId}`)
				.on(
					"postgres_changes",
					{
						event: "*",
						schema: "public",
						table: "shared",
						filter: `file_id=eq.${fileId}`,
					},
					(payload) => {
						const newRow = payload.new as Record<
							string,
							unknown
						> | null;
						const fileId = newRow?.file_id as string | undefined;
						if (fileId) {
							const isOwnFile =
								!!useJobStore.getState().labJobFilesLookup[
									fileId
								];
							if (!isOwnFile) {
								evictFiles([fileId]);
								if (newRow?.shared_until == null) {
									preloadFiles([fileId]);
								}
							}
						}
						if (jobIdRef.current) {
							fetchJobFiles(jobIdRef.current);
						}
					}
				)
				.subscribe()
		);

		return () => {
			channels.forEach((ch) => supabase.removeChannel(ch));
		};
	}, [allFileIds.join(",")]);

	const toFileGalleryItem = (
		file: (typeof allFiles)[number]
	): FileGalleryItem => {
		const cached = file.id ? fileCache[file.id] : null;
		return {
			id: file.id ?? "",
			file_name: cached?.file_name ?? file.file_name,
			path_name: cached?.path_name ?? file.path_name,
			url: cached?.signed_url ?? "",
			meta_data:
				cached?.meta_data ??
				(file.meta_data as unknown as Record<string, any> | null),
			shared_with: file.shared_with,
			shared_until: file.shared_until,
		};
	};

	const handleOpenFile = (fileId: string) => {
		setSelectedFileId(fileId);
		setFileViewerOpen(true);
	};

	const handleShareFile = async (fileId: string) => {
		if (!job?.id) return;
		await shareFile(fileId);
	};

	const handleUnshareFile = async (fileId: string) => {
		if (!job?.id) return;
		await unshareFile(fileId);
	};

	const handleDeleteFileClick = useCallback((fileId: string) => {
		setFileToDeleteId(fileId);
	}, []);

	const handleConfirmDelete = useCallback(async () => {
		if (!fileToDeleteId) return;
		setDeleting(true);
		const success = await hardDeleteFile(fileToDeleteId);
		if (success) {
			evictFiles([fileToDeleteId]);
		}
		setDeleting(false);
		setFileToDeleteId(null);
	}, [fileToDeleteId, hardDeleteFile, evictFiles]);

	const handleFileUpload = async (files: File[]) => {
		if (!job?.id) return;
		for (const file of files) {
			await uploadFile(file, job.id);
		}
	};

	const { isDragging, isUploading, dragHandlers } = useFileUpload({
		onFileUpload: handleFileUpload,
	});

	const size: FileGallerySize =
		previewSize ?? (isExpanded ? "large" : "small");
	const gapClass =
		size === "small" ? "gap-1.5" : size === "medium" ? "gap-2" : "gap-3";

	if (isLoadingFiles && !isDragging) {
		return (
			<div
				className="p-2 h-full"
				aria-busy="true"
				aria-label="Dateien werden geladen"
				data-testid="job-files-tab-loading"
				{...dragHandlers}
			>
				<div className={cn("flex flex-wrap", gapClass)}>
					{Array.from({ length: 3 }).map((_, i) => (
						<Skeleton
							key={i}
							className={SKELETON_DIMENSION[size]}
						/>
					))}
				</div>
			</div>
		);
	}

	if (allFileIds.length === 0 && !isDragging) {
		return (
			<div className="p-2 h-full" {...dragHandlers}>
				<div
					className={cn(
						"min-h-[80px] h-full flex items-center justify-center border-2 border-dashed rounded-md transition-colors",
						isDragging
							? "border-blue-400 bg-blue-50"
							: "border-gray-200"
					)}
				>
					<p className="text-xs text-gray-400">
						{isDragging
							? "Dateien hier ablegen"
							: "Dateien hierher ziehen"}
					</p>
				</div>
			</div>
		);
	}

	const renderFileGrid = (files: typeof labFiles, isMed = false) => (
		<div className={cn("flex flex-wrap", gapClass)}>
			{files.map((f) => (
				<FileInGallery
					key={f.id}
					file={toFileGalleryItem(f)}
					size={size}
					onFileClick={handleOpenFile}
					messageOfCurrentOrg={!isMed}
					alwaysShowPreview={!isMed}
					onShareFile={!isMed ? handleShareFile : undefined}
					onUnshareFile={!isMed ? handleUnshareFile : undefined}
					onDeleteFile={!isMed ? handleDeleteFileClick : undefined}
				/>
			))}
		</div>
	);

	return (
		<div
			className={cn(
				"p-2 h-full transition-colors",
				isDragging &&
					"bg-blue-50 border-2 border-dashed border-blue-400 rounded-md"
			)}
			{...dragHandlers}
		>
			{isDragging ? (
				<div className="flex items-center justify-center h-full min-h-[60px]">
					<p className="text-sm text-blue-600 font-medium">
						Dateien hier ablegen
					</p>
				</div>
			) : isUploading ? (
				<div className="flex items-center justify-center h-full min-h-[60px]">
					<p className="text-xs text-gray-500">Hochladen...</p>
				</div>
			) : (
				<div className="flex flex-col gap-2">
					{hasBothTypes && labFiles.length > 0 && (
						<div className="text-[10px] text-gray-400 uppercase tracking-wider">
							Labor
						</div>
					)}
					{labFiles.length > 0 && renderFileGrid(labFiles)}
					{hasBothTypes && medFiles.length > 0 && (
						<div className="text-[10px] text-gray-400 uppercase tracking-wider mt-1">
							Praxis
						</div>
					)}
					{medFiles.length > 0 && renderFileGrid(medFiles, true)}
				</div>
			)}
			{allFileIds.length > 0 && (
				<FileViewerModal
					fileIds={allFileIds}
					isOpen={fileViewerOpen}
					onClose={() => {
						setFileViewerOpen(false);
						setSelectedFileId(null);
					}}
					defaultFileId={selectedFileId || undefined}
					metadata={{
						jobId: job?.id ?? undefined,
						clientId: job?.client_id ?? undefined,
					}}
				/>
			)}
			<AlertDialog
				open={fileToDeleteId !== null}
				onOpenChange={(open) => {
					if (!open) setFileToDeleteId(null);
				}}
			>
				<AlertDialogContent data-testid="delete-file-dialog">
					<AlertDialogHeader>
						<AlertDialogTitle>
							Datei endgültig löschen?
						</AlertDialogTitle>
						<AlertDialogDescription>
							<span className="font-medium text-foreground">
								{fileToDeleteId
									? labJobFilesLookup[fileToDeleteId]
											?.file_name ?? "Datei"
									: "Datei"}
							</span>{" "}
							wird unwiderruflich gelöscht.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel
							disabled={deleting}
							data-testid="delete-file-dialog-cancel"
						>
							Abbrechen
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleConfirmDelete}
							disabled={deleting}
							className="bg-destructive text-white hover:bg-destructive/90"
							data-testid="delete-file-dialog-confirm"
						>
							{deleting
								? "Wird gelöscht..."
								: "Endgültig löschen"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
};
